import { ChangeEvent, Fragment, KeyboardEvent, TouchEvent, useEffect, useRef, useState } from "react";

import classNames from "classnames";
import useIsMobile from "shared/core/hooks/useIsMobile";

export const OTP_LENGTH = 6;

let currentOTPIndex: number = 0;

interface OTPInputProps {
  handleChange: (otp: string) => void;
}

const OTPInput = ({ handleChange }: OTPInputProps) => {
  const [otpList, setOtpList] = useState(new Array(OTP_LENGTH).fill(""));
  const [activeOTPIndex, setActiveOTPIndex] = useState(0);
  const [isOnChangeTriggered, setIsOnChangeTriggered] = useState(false);

  const xsMobile = useIsMobile(300);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleOnChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const value = target.value.replace(/\D/g, "");

    if (value.length >= OTP_LENGTH) {
      const filledOtpList = value.slice(0, OTP_LENGTH).split("");
      setOtpList(filledOtpList);
      setActiveOTPIndex(OTP_LENGTH - 1);
      handleChange(filledOtpList.join(""));
      return;
    }

    if (target.value && !value) {
      return;
    }

    setIsOnChangeTriggered(true);
    const newOtpList: string[] = [...otpList];
    newOtpList[currentOTPIndex] = value.substring(value.length - 1);

    if (!value) setActiveOTPIndex(currentOTPIndex - 1);
    else setActiveOTPIndex(currentOTPIndex + 1);

    setOtpList(newOtpList);
    handleChange(newOtpList.join(""));
    setIsOnChangeTriggered(false);
  };

  const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    currentOTPIndex = index;
    if (e.key === "Backspace") {
      setActiveOTPIndex(currentOTPIndex - 1);
      if (!isOnChangeTriggered) {
        const newOTP: string[] = [...otpList];
        newOTP[currentOTPIndex] = "";
        setOtpList(newOTP);
        handleChange(newOTP.join(""));
      }
    }
  };

  const handleOnTouchEnd = (e: TouchEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.currentTarget.focus({ preventScroll: true });
  };

  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, [activeOTPIndex]);

  return (
    <div className={"w-full flex justify-between items-center space-x-1 lg:space-x-4"}>
      {otpList.map((_, index) => {
        return (
          <Fragment key={index}>
            <input
              ref={activeOTPIndex === index ? inputRef : null}
              data-testid="otp-input"
              aria-label={`Please enter OTP character ${index + 1}`}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              className={classNames(
                "w-12 h-16 md:w-[70px] md:h-[80px] rounded-[6px] bg-l-blue text-center text-code spin-button-none focus:outline-brand-purple transition",
                { "w-8 h-12": xsMobile }
              )}
              onChange={handleOnChange}
              onKeyDown={(e) => handleOnKeyDown(e, index)}
              onTouchEnd={handleOnTouchEnd}
              value={otpList[index]}
            />
          </Fragment>
        );
      })}
    </div>
  );
};

export default OTPInput;
